import React, { useState } from 'react';
import Image from 'next/image';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Utils
import { MEDIA_LIBRARY_TYPES } from '@/lib/media/mediaLibraryTypes';

// Components
import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import ActionTag from '@/components/base/Tag/ActionTag';
import ContentIconBg from '@/components/icons/ContentIconBg';

import AddTags from './AddTags';

const Img = ({ src, mediaId, isVideo, loadingType }) => {
    const [errorImg, setErrorImage] = useState(false);
    if (errorImg || !src) {
        return (
            <div className="flex h-full w-full flex-1 items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon={isVideo ? 'play' : 'albums'}
                    className="text-4xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        );
    }

    return (
        <Image
            src={src}
            alt={mediaId}
            fill
            className="h-full w-full object-cover"
            sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
            onError={() => setErrorImage(true)}
            loading={loadingType}
        />
    );
};

const MediaTypeIcon = ({ icon = 'video' }) => {
    const { isDarkMode } = useDarkMode();
    return (
        <>
            <ContentIconBg className="absolute -top-0.5 right-0 z-1" darkMode={isDarkMode()} />
            <Icon
                aria-label="type-post"
                icon={icon}
                className="absolute right-0.5 top-0.5 z-1 align-middle text-xxs text-icoGray-500"
            />
        </>
    );
};

const MediaCard = ({
    media,
    availableTags,
    checked,
    onSelect,
    position,
    toggleMVL,
    mutateLibraryMedia
}) => {
    const { t } = useTranslation();
    // A bit strange to store a ref in state but apparently that's what you do when you want to get notify of a ref change.
    // https://stackoverflow.com/a/59605067/11298026
    // Otherwise AddTags container is null at the first render and thus the popover doesn't open.
    const [mediaCardRef, setMediaCardRef] = useState(null);

    const displayedTag = media?.tags?.[0];
    const numberRemainingTags = media?.tags?.length - 1;

    return (
        <div className="group relative" ref={(ref) => setMediaCardRef(ref)}>
            <div
                className="relative cursor-pointer rounded-lg bg-white p-2 dark:bg-icoDarkMode-anthracite"
                onClick={toggleMVL}
                onKeyDown={toggleMVL}
                role="button"
                tabIndex={0}>
                <div className="relative overflow-hidden rounded-md after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/50 after:opacity-0 after:transition-opacity group-hover:after:opacity-100">
                    <div
                        className="square relative bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"
                        data-testid={`media-${media.id}`}
                        data-media-type={media.media_type}>
                        <Img
                            src={
                                media?.medias?.[0]?.min_thumbnail ??
                                media?.medias?.[0]?.image ??
                                media?.medias?.[0]?.thumbnail
                            }
                            mediaId={media.id}
                            isVideo={media.media_type === MEDIA_LIBRARY_TYPES.TYPE_VIDEO}
                            loadingType={position > 10 ? 'eager' : 'lazy'}
                        />
                    </div>
                    {media.media_type === MEDIA_LIBRARY_TYPES.TYPE_VIDEO && <MediaTypeIcon />}
                    {media.media_type === MEDIA_LIBRARY_TYPES.TYPE_DOCUMENT && (
                        <MediaTypeIcon icon="document" />
                    )}
                </div>
            </div>
            <Checkbox
                onChange={() => {
                    onSelect(media.id);
                }}
                value="checked"
                className="absolute right-4 top-4 z-1 checked:opacity-100 group-hover:opacity-100 md:opacity-0"
                boxSize="large"
                checked={checked}
            />
            <div className="absolute bottom-4 left-4 mb-2 ml-4 flex w-full items-center gap-2 group-hover:opacity-100 sm:opacity-0">
                {media?.tags?.length > 0 ? (
                    <>
                        <ActionTag
                            className="!containerrounded-2xl inline-flex h-8 max-w-[56px] items-center px-xxs py-2 text-xs capitalize"
                            variant="media">
                            <span className="truncate">{displayedTag.tag}</span>
                        </ActionTag>
                        {media.tags.length > 1 && (
                            <div className="flex h-8 w-8 items-center justify-around rounded-full bg-black/20 p-2 text-xs text-white backdrop-blur-20">
                                +{numberRemainingTags}
                            </div>
                        )}
                    </>
                ) : null}
                <AddTags
                    selectedMediaIds={[media.id]}
                    availableTags={availableTags}
                    mediaTags={media.tags}
                    mutateLibraryMedia={mutateLibraryMedia}
                    container={mediaCardRef}
                    renderButton={(setReferenceElement, toggle) => (
                        <div ref={setReferenceElement}>
                            <ActionTag
                                className="h-8 cursor-pointer"
                                variant="media"
                                onClick={toggle}
                                afterIcon="plus"
                                title={t('media:tags_popover.add_tag')}>
                                <Icon icon="labels" />
                            </ActionTag>
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default MediaCard;
